"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useParams } from "next/navigation";
import { cn } from "@/lib/utils";

interface CallToActionProps {
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  image: string;
  imageAlt: string;
  spacingTop?: boolean;
  spacingBottom?: boolean;
}

export const CallToAction = ({
  title,
  description,
  buttonText,
  buttonLink,
  image,
  imageAlt,
  spacingTop = false,
  spacingBottom = false,
}: CallToActionProps) => {
  const { locale } = useParams();
  return (
    <section
      className={cn(
        "relative w-full overflow-hidden",
        spacingTop ? "mt-14 lg:mt-28" : "-mt-14 lg:-mt-28",
        spacingBottom && "mb-14 lg:mb-28",
      )}
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <Image
          src={image}
          alt={imageAlt || "Image"}
          fill
          className="object-cover"
          priority
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center justify-center">
        <div className="w-full max-w-7xl mx-auto px-4 flex">
          <div className="max-w-4xl mx-auto text-center text-white py-14 lg:py-28">
            <h2 className="text-xl lg:text-3xl font-bold mb-6">{title}</h2>
            <p className="text-base text-white/90 mb-8 leading-relaxed">
              {description}
            </p>
            <div className="flex justify-center">
              <Link
                className="bg-white text-black px-6 py-3 flex items-center justify-center gap-3 rounded-lg hover:bg-white/90 transition-all duration-300 hover:scale-105 ease-in-out font-medium text-sm"
                href={buttonLink}
                locale={locale as string}
              >
                {buttonText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
